import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Input, Select, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DATE_PICKER_FORMAT, DEFAULT_VALUE } from "@/config";
import { INVOICE_STATE, RECONCILIATION_STATE, RECONCILIATION_TYPE } from "@/meta/purchase/reconciliation";
import { dateFormat, getRealyState } from "@/utils";
import { getEnterpriseName } from "@/utils/customer";
import { formatNumber } from "@/utils/number";
import Permission from "@/views/components/Permission";
import { useUserStore } from "@/views/stores";

export interface PurchaseReconciliationRow {
    id?: number | string;
    projectName?: string;
    type?: number | string;
    supplierEnterprise?: { name?: string; realName?: string; mobile?: string };
    purchaseEnterprise?: { name?: string; realName?: string; mobile?: string };
    calculatedAmount?: number;
    actualAmount?: number;
    reconciliationDate?: string;
    invoiceState?: number | string;
    state?: number | string;
}

export interface PurchaseReconciliationQuery {
    page: number;
    size: number;
    param?: string;
    type?: number;
    enterprise?: string;
    invoiceState?: number;
    state?: number[];
    sortKey?: string;
    sortOrder?: string;
}

export interface PurchaseReconciliationPageResult {
    list?: PurchaseReconciliationRow[];
    total?: number;
}

export interface PurchaseReconciliationPageProps {
    loadReconciliations?: (query: PurchaseReconciliationQuery) => Promise<PurchaseReconciliationPageResult>;
    closeReconciliation?: (id: number | string) => Promise<unknown>;
}

// 对齐旧 Vue useTable 的默认分页契约，采购对账列表首屏每页展示 10 条。
const defaultQuery: PurchaseReconciliationQuery = { page: 1, size: 10 };

/** 复用旧 Vue reconciliationService 的对账分页接口，保持请求路径和参数契约。 */
export async function loadPurchaseReconciliations(
    query: PurchaseReconciliationQuery
): Promise<PurchaseReconciliationPageResult> {
    const { data } = await request.service.get<PurchaseReconciliationPageResult>(
        "/admin/purchase/reconciliation/paging",
        { params: query }
    );
    return data;
}

/** 复用 reconciliationService.closeById 的关闭接口路径，由页面确认后调用。 */
export async function closePurchaseReconciliation(id: number | string): Promise<void> {
    await request.service.put(`/admin/purchase/reconciliation/close/${id}`);
}

/** 将旧 Vue ProTable 的筛选草稿转换为分页接口参数，并清理空值。 */
export function buildPurchaseReconciliationQuery(values: {
    param?: string;
    type?: number | string;
    enterprise?: string;
    invoiceState?: number | string;
    state?: Array<number | string>;
    sortKey?: string;
    sortOrder?: string;
}): PurchaseReconciliationQuery {
    return {
        ...defaultQuery,
        ...(values.param?.trim() ? { param: values.param.trim() } : {}),
        ...(values.type == null ? {} : { type: Number(values.type) }),
        ...(values.enterprise?.trim() ? { enterprise: values.enterprise.trim() } : {}),
        ...(values.invoiceState == null ? {} : { invoiceState: Number(values.invoiceState) }),
        ...(values.state?.length ? { state: values.state.map(Number) } : {}),
        ...(values.sortKey ? { sortKey: values.sortKey } : {}),
        ...(values.sortOrder ? { sortOrder: values.sortOrder } : {})
    };
}

/** 显示对账类型或状态的旧 Vue 枚举文案。 */
function enumLabel(meta: { getIntroByCode: (code?: number | string) => string }, value?: number | string): string {
    return value == null ? DEFAULT_VALUE : meta.getIntroByCode(Number(value)) || DEFAULT_VALUE;
}

/** 对账状态展示使用旧页面的高位真实状态规则。 */
function reconciliationStateCode(value?: number | string): number {
    const rawState = Number(value);
    const realState = getRealyState(rawState);

    // 旧接口存在已解码原值和低位签署态两种返回，优先使用 Vue 解码值并兼容这两种原值。
    if (realState === 1 || rawState === 1) return RECONCILIATION_STATE.SIGNING.code;
    if (RECONCILIATION_STATE.getIntroByCode(realState)) return realState;
    return rawState;
}

/** 将状态码通过旧 Vue 枚举转换为展示文案。 */
function reconciliationStateLabel(value?: number | string): string {
    return enumLabel(RECONCILIATION_STATE, reconciliationStateCode(value));
}

/** 仅已完成记录展示开票状态，和旧 Vue formatter 保持一致。 */
function invoiceStateLabel(row: PurchaseReconciliationRow): string {
    if (reconciliationStateCode(row.state) === RECONCILIATION_STATE.COMPLETED.code && row.invoiceState != null) {
        return enumLabel(INVOICE_STATE, row.invoiceState);
    }
    return DEFAULT_VALUE;
}

/** 采购对账列表：覆盖旧 Vue 的筛选、排序、详情入口和条件关闭动作。 */
export function PurchaseReconciliationPage({
    loadReconciliations: load = loadPurchaseReconciliations,
    closeReconciliation = closePurchaseReconciliation
}: PurchaseReconciliationPageProps) {
    const banButton = useUserStore(state => state.banButton);
    const hasClosePermission = useUserStore(state => state.hasAnyPermission(["ROLE_PROJECT_MANAGE"]));
    const [query, setQuery] = useState<PurchaseReconciliationQuery>(defaultQuery);
    const [param, setParam] = useState("");
    const [type, setType] = useState<number>();
    const [enterprise, setEnterprise] = useState("");
    const [invoiceState, setInvoiceState] = useState<number>();
    const [state, setState] = useState<number[]>([]);
    const [rows, setRows] = useState<PurchaseReconciliationRow[]>([]);
    const [total, setTotal] = useState(0);
    const [reloadToken, setReloadToken] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        void load(query)
            .then(result => {
                if (!active) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (!active) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("采购对账加载失败");
            });
        return () => {
            active = false;
        };
    }, [load, query, reloadToken]);

    const submitFilters = () =>
        setQuery(buildPurchaseReconciliationQuery({ param, type, enterprise, invoiceState, state }));

    const resetFilters = () => {
        setParam("");
        setType(undefined);
        setEnterprise("");
        setInvoiceState(undefined);
        setState([]);
        setQuery(defaultQuery);
    };

    // 将 zhihao-ui-react 排序回调映射为旧 Vue reconciliationService 的日期参数。
    const handleSortChange = ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
        const nextQuery = { ...query, page: 1 };
        delete nextQuery.sortKey;
        delete nextQuery.sortOrder;
        if (prop === "reconciliationDate" && order) {
            nextQuery.sortKey = "reconciliation_date";
            nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
        }
        setQuery(nextQuery);
    };

    const handleClose = async (id: number | string) => {
        try {
            await closeReconciliation(id);
            setReloadToken(value => value + 1);
        } catch {
            setErrorMessage("采购对账关闭失败");
        }
    };

    // 用 zhihao-ui-react 列契约承接 Vue 对账列表的字段、状态、排序和权限操作。
    const columns: TableColumnProps<PurchaseReconciliationRow>[] = [
        {
            key: "id",
            label: "编号",
            minWidth: 145,
            prop: "id",
            render: ({ row }) =>
                row.id == null ? (
                    DEFAULT_VALUE
                ) : (
                    <Link to={`/purchasingManage/reconciliation/info/${row.id}`}>{String(row.id)}</Link>
                )
        },
        {
            key: "projectName",
            label: "项目",
            minWidth: 228,
            prop: "projectName",
            render: ({ row }) => row.projectName || DEFAULT_VALUE
        },
        {
            key: "type",
            label: "类型",
            minWidth: 165,
            prop: "type",
            render: ({ row }) => enumLabel(RECONCILIATION_TYPE, row.type)
        },
        {
            key: "supplierEnterprise",
            label: "供货单位",
            minWidth: 228,
            prop: "supplierEnterprise",
            render: ({ row }) => getEnterpriseName(row.supplierEnterprise as never)
        },
        {
            key: "purchaseEnterprise",
            label: "对账单位",
            minWidth: 214,
            prop: "purchaseEnterprise",
            render: ({ row }) => getEnterpriseName(row.purchaseEnterprise as never)
        },
        {
            key: "calculatedAmount",
            label: "核算金额 (元)",
            minWidth: 130,
            prop: "calculatedAmount",
            render: ({ row }) => formatNumber(row.calculatedAmount, 2)
        },
        {
            key: "actualAmount",
            label: "折扣金额 (元)",
            minWidth: 130,
            prop: "actualAmount",
            render: ({ row }) => formatNumber(row.actualAmount, 2)
        },
        {
            key: "reconciliationDate",
            label: "日期",
            minWidth: 150,
            prop: "reconciliationDate",
            sortable: true,
            render: ({ row }) => dateFormat(row.reconciliationDate, DATE_PICKER_FORMAT)
        },
        {
            key: "invoiceState",
            label: "是否已开票",
            width: 120,
            prop: "invoiceState",
            render: ({ row }) => invoiceStateLabel(row)
        },
        {
            key: "state",
            label: "对账状态",
            width: 100,
            prop: "state",
            render: ({ row }) => reconciliationStateLabel(row.state)
        },
        {
            key: "operation",
            label: "操作",
            prop: "operation",
            render: ({ row }) => {
                const hasCloseAction =
                    hasClosePermission &&
                    row.state === RECONCILIATION_STATE.COMPLETED.code &&
                    row.invoiceState === INVOICE_STATE.NOT_INVOICE.code;
                const canClose = hasCloseAction && !banButton;
                return hasCloseAction ? (
                    <Permission anyOf={["ROLE_PROJECT_MANAGE"]}>
                        <Button
                            aria-label="关闭"
                            disabled={!canClose}
                            type="link"
                            onClick={() => void handleClose(row.id as number | string)}
                        >
                            关闭
                        </Button>
                    </Permission>
                ) : null;
            }
        }
    ];

    return (
        <section aria-label="purchase-reconciliation-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<PurchaseReconciliationRow>
                    data={rows}
                    columns={normalizeOperationColumns(columns, rows)}
                    pagination={false}
                    rowKey={row => String(row.id)}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                采购对账
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <div className="mb-4 flex flex-wrap items-center gap-2">
                                <Input
                                    aria-label="项目名称或编号"
                                    maxLength={32}
                                    placeholder="请输入项目名称或编号搜索"
                                    value={param}
                                    onChange={event => setParam(event.target.value)}
                                />
                                <Select
                                    aria-label="对账类型"
                                    allowClear
                                    options={RECONCILIATION_TYPE.options}
                                    placeholder="请选择类型"
                                    value={type}
                                    onChange={value => setType(value)}
                                />
                                <Input
                                    aria-label="供货单位"
                                    maxLength={32}
                                    placeholder="请输入单位名"
                                    value={enterprise}
                                    onChange={event => setEnterprise(event.target.value)}
                                />
                                <Select
                                    aria-label="是否已开票"
                                    allowClear
                                    options={INVOICE_STATE.options}
                                    placeholder="请选择是否已开票"
                                    value={invoiceState}
                                    onChange={value => setInvoiceState(value)}
                                />
                                <Select
                                    aria-label="对账状态"
                                    allowClear
                                    mode="multiple"
                                    options={RECONCILIATION_STATE.options}
                                    placeholder="请选择对账状态"
                                    value={state}
                                    onChange={value => setState(value)}
                                />
                                <Button type="primary" onClick={submitFilters}>
                                    查询
                                </Button>
                                <Button onClick={resetFilters}>重置</Button>
                            </div>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                // 对齐旧版 Element Plus 分页条数选项，切换条数后回到第一页。
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                hideOnSinglePage
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default PurchaseReconciliationPage;
